import { useState, useRef, useEffect, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Combobox } from '@/components/ui/combobox';
import { Input } from '@/components/ui/input';
import { PasswordInput } from '@/components/ui/password-input';
import { Spinner } from '@/components/ui/spinner';
import { StatusBadge } from '@/components/ui/status-badge';
import { AlertNotice } from '@/components/ui/alert-notice';
import { useAppDispatch } from '@/state/store';
import {
  setOnboardingEmails,
  setOnboardingBatchInputs,
} from '@/state/slices/onboardingSlice';
import OnboardBgWrapper from '../backgroundImage/OnboardBgWrapper';
import {
  ApiErrorAlert,
  CascadeSection,
  ChangePlatformButton,
  FooterButtons,
  FormField,
  FormRow,
  InfoAlert,
  OnboardCard,
  OnboardCardBody,
  OnboardCardHeader,
  OnboardingPage,
  PatRow,
  SectionDivider,
  TechStackSection,
  TestScriptLoadSection,
  TestSection,
} from './onboarding-layout';
import OnboardingPageHeader from './OnboardingPageHeader';
import { getApiErrorMessage } from '@/utils';
import type { PipelinePlatform } from '@/types/pipeline-platform';
import { PIPELINE_PLATFORM_OPTIONS } from '@/types/pipeline-platform';
import {
  fetchJenkinsJobs,
  type JenkinsJob,
} from '@/components/smartai/api/jenkinsOnboarding';
import { filterNonTestFiles } from '@/utils/filterFilePaths';
import type { CodeAnalysisResult } from '@/utils/filterFilePaths';
import type { FileItem } from '@/components/smartai/api/detectRepository';
import {
  enableRepositoryCapture,
  validateCaptureUrl,
} from '@/components/smartai/api/captureApi';
import FileExclusionsCard from './FileExclusionsCard';
import ScanCard from './ScanCard';
import TestScriptSelectionCard from './TestScriptSelectionCard';
import ScriptIndexProgressCard, {
  type ScriptIndexProgress,
} from './ScriptIndexProgressCard';
import { getTestScriptIndexStatus } from '@/components/smartai/api/getTestScriptIndexStatus';
import type { TestScriptIndexStatusPayload } from '@/components/smartai/api/getTestScriptIndexStatus';
import {
  detectGithubRepository,
  getGithubBranches,
  getGithubEnvironments,
  getGithubLatestDeployment,
  getGithubOrganizations,
  getGithubRepositories,
  getGithubTestScripts,
  getGithubWorkflowJobs,
  getGithubWorkflows,
  type GithubBranch,
  type GithubDeployment,
  type GithubDetectBody,
  type GithubEnvironment,
  type GithubOrganization,
  type GithubRepository,
  type GithubTestScriptCandidate,
  type GithubWorkflow,
  type GithubWorkflowJob,
} from '@/components/smartai/api/githubOnboarding';

import type { ScmProviderId } from '@/types/scm-provider';
import type { DetectRepoOnboardPrefill } from '@/components/smartai/DetectRepo/detectRepoOnboardPrefill';

// The user account onboarding runs as. Mirrors the ADO view's placeholder.
const ONBOARDED_BY = 'developer@example.com';

const EMPTY_SCRIPT_INDEX_PROGRESS: ScriptIndexProgress = {
  processed: 0,
  total: 0,
  percentage: 0,
};

function buildScriptIndexProgress(
  data: TestScriptIndexStatusPayload,
): ScriptIndexProgress {
  const total = data.totalCount ?? data.selectedCount ?? 0;
  const processed = data.processedCount ?? 0;
  const percentage =
    total > 0 ? Math.min(100, Math.round((processed / total) * 100)) : 0;
  return {
    processed,
    total,
    percentage,
    phase: data.phase,
  };
}

interface GitHubOnboardingViewProps {
  provider: ScmProviderId;
  onChangeProvider: () => void;
  /** Prefill from Detect Repo onboard / Onboard Both Repositories. */
  detectPrefill?: DetectRepoOnboardPrefill;
  /** PAT already accepted on the Add Repository connect panel. */
  autoConnectPat?: string;
  /** Organisation selected on the Add Repository connect panel. */
  connectOrganization?: string;
}

type Option = { value: string; label: string };

function isValidOptionalUrl(value: string): boolean {
  if (!value.trim()) return true;
  try {
    new URL(value);
    return true;
  } catch {
    return false;
  }
}

function validateDeploymentUrls(
  frontendUrl: string,
  backendUrl: string,
): { frontendUrl?: string; backendUrl?: string } | null {
  const frontend = frontendUrl.trim();
  const backend = backendUrl.trim();
  const errors: { frontendUrl?: string; backendUrl?: string } = {};

  if (!frontend && !backend) {
    errors.frontendUrl = 'At least one URL (Frontend or Backend) is required';
    return errors;
  }
  if (frontend && !isValidOptionalUrl(frontend)) {
    errors.frontendUrl = 'Enter a valid URL';
  }
  if (backend && !isValidOptionalUrl(backend)) {
    errors.backendUrl = 'Enter a valid URL';
  }

  return Object.keys(errors).length > 0 ? errors : null;
}

// ── Small presentational helpers ─────────────────────────────────────────────

function SelectField({
  label,
  value,
  options,
  onSelect,
  disabled,
  loading,
  placeholder,
}: {
  label: string;
  value: string;
  options: Option[];
  onSelect: (val: string) => void;
  disabled?: boolean;
  loading?: boolean;
  placeholder?: string;
}) {
  return (
    <FormField label={label} loading={loading ? <Spinner compact /> : undefined}>
      <Combobox
        data={options}
        value={value}
        onSelect={(val: string | number | (string | number)[]) =>
          onSelect(String(val))
        }
        placeholder={placeholder ?? `Select ${label}`}
        disabled={disabled}
      />
    </FormField>
  );
}

// ── Main view ────────────────────────────────────────────────────────────────

const GitHubOnboardingView = ({
  provider,
  onChangeProvider,
  detectPrefill,
  autoConnectPat,
  connectOrganization,
}: GitHubOnboardingViewProps) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  // Applied once after the user clicks Fetch Organizations.
  const detectPrefillRef = useRef<DetectRepoOnboardPrefill | undefined>(
    detectPrefill,
  );
  const detectPrefillAppliedRef = useRef(false);

  // ── Application repository state ────────────────────────────────────────
  const [pat, setPat] = useState(
    () => autoConnectPat ?? detectPrefill?.pat ?? '',
  );
  const [orgs, setOrgs] = useState<GithubOrganization[]>([]);
  const [selectedOrg, setSelectedOrg] = useState('');
  const [repos, setRepos] = useState<GithubRepository[]>([]);
  const [selectedRepo, setSelectedRepo] = useState('');
  const [branches, setBranches] = useState<GithubBranch[]>([]);
  const [selectedBranch, setSelectedBranch] = useState('');
  const [workflows, setWorkflows] = useState<GithubWorkflow[]>([]);
  const [selectedWorkflow, setSelectedWorkflow] = useState('');
  const [jobs, setJobs] = useState<GithubWorkflowJob[]>([]);
  const [selectedJob, setSelectedJob] = useState('');
  const [environments, setEnvironments] = useState<GithubEnvironment[]>([]);
  const [selectedEnvironment, setSelectedEnvironment] = useState('');
  const [deployment, setDeployment] = useState<GithubDeployment | null>(null);
  const [frontendUrl, setFrontendUrl] = useState('');
  const [backendUrl, setBackendUrl] = useState('');
  const [urlErrors, setUrlErrors] = useState<{
    frontendUrl?: string;
    backendUrl?: string;
  }>({});
  const [captureBeforeAfter, setCaptureBeforeAfter] = useState(false);
  const [captureValidating, setCaptureValidating] = useState(false);
  const [captureUrlError, setCaptureUrlError] = useState<string | null>(null);
  const [captureUrlOk, setCaptureUrlOk] = useState(false);
  const captureValidateTimer = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );

  useEffect(
    () => () => {
      if (captureValidateTimer.current) {
        clearTimeout(captureValidateTimer.current);
      }
    },
    [],
  );

  // ── Loading / error flags ───────────────────────────────────────────────
  const [orgLoading, setOrgLoading] = useState(false);
  const [reposLoading, setReposLoading] = useState(false);
  const [branchesLoading, setBranchesLoading] = useState(false);
  const [workflowsLoading, setWorkflowsLoading] = useState(false);
  const [jobsLoading, setJobsLoading] = useState(false);
  const [envLoading, setEnvLoading] = useState(false);
  const [deploymentLoading, setDeploymentLoading] = useState(false);
  const [fetchError, setFetchError] = useState<string | null>(null);

  // ── Test repository state ───────────────────────────────────────────────
  const [testEnabled, setTestEnabled] = useState(false);
  const [testPat, setTestPat] = useState('');
  const [testOrgs, setTestOrgs] = useState<GithubOrganization[]>([]);
  const [selectedTestOrg, setSelectedTestOrg] = useState('');
  const [testRepos, setTestRepos] = useState<GithubRepository[]>([]);
  const [selectedTestRepo, setSelectedTestRepo] = useState('');
  const [testBranches, setTestBranches] = useState<GithubBranch[]>([]);
  const [selectedTestBranch, setSelectedTestBranch] = useState('');
  const [testWorkflows, setTestWorkflows] = useState<GithubWorkflow[]>([]);
  const [selectedTestWorkflow, setSelectedTestWorkflow] = useState('');
  const [testJobs, setTestJobs] = useState<GithubWorkflowJob[]>([]);
  const [selectedTestJob, setSelectedTestJob] = useState('');
  const [testPipelinePlatform, setTestPipelinePlatform] =
    useState<PipelinePlatform | ''>('');
  const [jenkinsBaseUrl, setJenkinsBaseUrl] = useState('');
  const [jenkinsUsername, setJenkinsUsername] = useState('');
  const [jenkinsApiToken, setJenkinsApiToken] = useState('');
  const [jenkinsJobName, setJenkinsJobName] = useState('');
  const [jenkinsJobs, setJenkinsJobs] = useState<JenkinsJob[]>([]);
  const [jenkinsJobsLoading, setJenkinsJobsLoading] = useState(false);
  const [jenkinsJobsError, setJenkinsJobsError] = useState<string | null>(null);
  const [testOrgLoading, setTestOrgLoading] = useState(false);
  const [testReposLoading, setTestReposLoading] = useState(false);
  const [testBranchesLoading, setTestBranchesLoading] = useState(false);
  const [testWorkflowsLoading, setTestWorkflowsLoading] = useState(false);
  const [testJobsLoading, setTestJobsLoading] = useState(false);

  // ── Test scripts ────────────────────────────────────────────────────────
  const [scriptCandidates, setScriptCandidates] = useState<
    GithubTestScriptCandidate[]
  >([]);
  const [selectedScriptPaths, setSelectedScriptPaths] = useState<Set<string>>(
    new Set(),
  );
  const [scriptsLoading, setScriptsLoading] = useState(false);
  const [scriptsLoaded, setScriptsLoaded] = useState(false);
  const [scriptsError, setScriptsError] = useState<string | null>(null);

  const [scriptIndexStatus, setScriptIndexStatus] = useState<
    'idle' | 'processing' | 'completed' | 'failed' | 'skipped'
  >('idle');
  const [scriptIndexMessage, setScriptIndexMessage] = useState<string | null>(
    null,
  );
  const [scriptIndexProgress, setScriptIndexProgress] =
    useState<ScriptIndexProgress>(EMPTY_SCRIPT_INDEX_PROGRESS);

  // ── Detect / post-detect state ──────────────────────────────────────────
  const [detectLoading, setDetectLoading] = useState(false);
  const [detectError, setDetectError] = useState<string | null>(null);
  const [dbRepoId, setDbRepoId] = useState('');
  const [dbBranchId, setDbBranchId] = useState('');
  const [analysisResult, setAnalysisResult] =
    useState<CodeAnalysisResult | null>(null);
  const [selectedFiles, setSelectedFiles] = useState<Set<FileItem>>(new Set());
  const [showPostDetect, setShowPostDetect] = useState(false);

  // ── Email state (for FileExclusionsCard + scan) ──────────────────────────
  const [prEmails, setPrEmails] = useState<string[]>([]);
  const [autoPrEmails, setAutoPrEmails] = useState<string[]>([]);
  const [skipPrEmails, setSkipPrEmails] = useState(false);
  const [skipAutoPrEmails, setSkipAutoPrEmails] = useState(false);
  const [prEmailsError, setPrEmailsError] = useState('');
  const [autoPrEmailsError, setAutoPrEmailsError] = useState('');

  const exclusionsRef = useRef<HTMLDivElement | null>(null);

  // ── Derived helpers ───────────────────────────────────────────────────────
  const selectedRepoObj = repos.find(r => r.name === selectedRepo);
  const selectedWorkflowObj = workflows.find(
    w => String(w.id) === selectedWorkflow,
  );
  const selectedTestRepoObj = testRepos.find(r => r.name === selectedTestRepo);
  const selectedTestWorkflowObj = testWorkflows.find(
    w => String(w.id) === selectedTestWorkflow,
  );

  const toErr = (e: unknown, fallback: string) =>
    getApiErrorMessage(e, fallback) || fallback;

  useEffect(() => {
    if (
      !showPostDetect ||
      !testEnabled ||
      !dbRepoId ||
      scriptIndexStatus !== 'processing'
    )
      return;

    let cancelled = false;

    const poll = async () => {
      try {
        const res = await getTestScriptIndexStatus(dbRepoId);
        if (cancelled) return;
        const data = res.data;
        if (!res.success || !data) return;

        const st = String(data.status ?? '').toLowerCase();
        if (st === 'completed') {
          setScriptIndexStatus('completed');
          setScriptIndexMessage(
            data.message ?? `Indexed ${data.inserted ?? 0} test script(s)`,
          );
          setScriptIndexProgress(buildScriptIndexProgress(data));
        } else if (st === 'failed') {
          setScriptIndexStatus('failed');
          setScriptIndexMessage(
            data.error || data.message || 'Test script indexing failed',
          );
        } else if (st === 'processing' || st === 'running') {
          setScriptIndexMessage(
            data.message ?? 'Indexing test scripts in background…',
          );
          setScriptIndexProgress(buildScriptIndexProgress(data));
        }
      } catch {
        if (!cancelled) {
          setScriptIndexMessage(
            'Could not refresh test script index status. Retrying…',
          );
        }
      }
    };

    void poll();
    const timer = setInterval(() => void poll(), 2500);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [dbRepoId, scriptIndexStatus, showPostDetect, testEnabled]);

  // ── Application cascade handlers ──────────────────────────────────────────

  /**
   * After Fetch Organizations succeeds, apply Detect Repo prefill for the
   * application repo (org → repo → branch), open Connect Test Repository, and
   * when present also prefill test-repository fields from
   * "Onboard Both Repositories".
   */
  async function applyDetectPrefill(orgList: GithubOrganization[], token: string) {
    const pf = detectPrefillRef.current;
    if (!pf || detectPrefillAppliedRef.current) return;
    detectPrefillAppliedRef.current = true;

    const orgLogin =
      orgList.find(
        o => o.login.toLowerCase() === pf.organization.toLowerCase(),
      )?.login ?? pf.organization;

    setSelectedOrg(orgLogin);
    resetBelowOrg();
    setReposLoading(true);
    try {
      const repoRes = await getGithubRepositories(provider, token, orgLogin);
      const repoList = repoRes.data?.repositories ?? [];
      setRepos(repoList);

      if (pf.repository) {
        const repoName =
          repoList.find(r => r.name === pf.repository)?.name ?? pf.repository;
        setSelectedRepo(repoName);

        setBranchesLoading(true);
        setWorkflowsLoading(true);
        setEnvLoading(true);
        try {
          const [branchRes, workflowRes, envRes] = await Promise.all([
            getGithubBranches(provider, token, orgLogin, repoName),
            getGithubWorkflows(provider, token, orgLogin, repoName),
            getGithubEnvironments(provider, token, orgLogin, repoName),
          ]);
          const branchList = branchRes.data?.branches ?? [];
          setBranches(branchList);
          setWorkflows(workflowRes.data?.workflows ?? []);
          setEnvironments(envRes.data?.environments ?? []);

          // Only autofill branch when Detect Repo had one selected — never force default.
          const wantedBranch = pf.branch?.trim();
          if (wantedBranch && branchList.some(b => b.name === wantedBranch)) {
            setSelectedBranch(wantedBranch);
          }
        } finally {
          setBranchesLoading(false);
          setWorkflowsLoading(false);
          setEnvLoading(false);
        }
      }
    } catch (e) {
      setFetchError(toErr(e, 'Failed to apply Detect Repo selection'));
    } finally {
      setReposLoading(false);
    }

    // Always open the test section from Detect Repo.
    setTestEnabled(true);

    // Prefill test repository when coming from "Onboard Both Repositories".
    const testToken = (pf.testPat || token).trim();
    const testOrgWanted = (pf.testOrganization || '').trim();
    const testRepoWanted = (pf.testRepository || '').trim();
    if (!testToken || (!testOrgWanted && !testRepoWanted)) {
      return;
    }

    setTestPat(testToken);
    setTestOrgLoading(true);
    try {
      const testOrgRes = await getGithubOrganizations(provider, testToken);
      const testOrgList = testOrgRes.data?.organizations ?? [];
      setTestOrgs(testOrgList);

      const testOrgLogin =
        testOrgList.find(
          o => o.login.toLowerCase() === (testOrgWanted || pf.organization).toLowerCase(),
        )?.login ?? (testOrgWanted || pf.organization);

      setSelectedTestOrg(testOrgLogin);
      setSelectedTestRepo('');
      setSelectedTestBranch('');
      setSelectedTestWorkflow('');
      setSelectedTestJob('');
      setTestRepos([]);
      setTestBranches([]);
      setTestWorkflows([]);
      setTestJobs([]);
      resetScriptState();

      if (!testRepoWanted) return;

      setTestReposLoading(true);
      try {
        const testRepoRes = await getGithubRepositories(
          provider,
          testToken,
          testOrgLogin,
        );
        const testRepoList = testRepoRes.data?.repositories ?? [];
        setTestRepos(testRepoList);

        const testRepoName =
          testRepoList.find(r => r.name === testRepoWanted)?.name ??
          testRepoWanted;
        setSelectedTestRepo(testRepoName);

        setTestBranchesLoading(true);
        setTestWorkflowsLoading(true);
        try {
          const [branchRes, workflowRes] = await Promise.all([
            getGithubBranches(provider, testToken, testOrgLogin, testRepoName),
            getGithubWorkflows(provider, testToken, testOrgLogin, testRepoName),
          ]);
          const testBranchList = branchRes.data?.branches ?? [];
          setTestBranches(testBranchList);
          setTestWorkflows(workflowRes.data?.workflows ?? []);

          const wantedTestBranch = pf.testBranch?.trim();
          if (
            wantedTestBranch &&
            testBranchList.some(b => b.name === wantedTestBranch)
          ) {
            setSelectedTestBranch(wantedTestBranch);
          }
        } finally {
          setTestBranchesLoading(false);
          setTestWorkflowsLoading(false);
        }
      } finally {
        setTestReposLoading(false);
      }
    } catch (e) {
      setScriptsError(toErr(e, 'Failed to apply Detect Repo test selection'));
    } finally {
      setTestOrgLoading(false);
    }
  }

  async function handleFetchOrganizations() {
    if (!pat || pat.length < 5) return;
    setFetchError(null);
    setOrgLoading(true);
    resetApplicationCascade();
    setOrgs([]);
    try {
      const res = await getGithubOrganizations(provider, pat);
      const orgList = res.data?.organizations ?? [];
      setOrgs(orgList);
      await applyDetectPrefill(orgList, pat);
    } catch (e) {
      setFetchError(toErr(e, 'Failed to fetch organizations'));
    } finally {
      setOrgLoading(false);
    }
  }

  const autoFetchedRef = useRef(false);
  useEffect(() => {
    if (!autoConnectPat?.trim() || autoFetchedRef.current) return;
    if (!pat || pat.length < 5) return;
    autoFetchedRef.current = true;
    void handleFetchOrganizations();
    // Fetch once after Connect from the Add Repository panel.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoConnectPat, pat]);

  const connectOrgAppliedRef = useRef(false);
  useEffect(() => {
    if (!connectOrganization || connectOrgAppliedRef.current) return;
    if (!orgs.some(org => org.login === connectOrganization)) return;
    connectOrgAppliedRef.current = true;
    void handleSelectOrg(connectOrganization);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [connectOrganization, orgs]);

  async function handleSelectOrg(login: string) {
    if (!login || login === selectedOrg) return;
    setSelectedOrg(login);
    resetBelowOrg();
    setReposLoading(true);
    try {
      const res = await getGithubRepositories(provider, pat, login);
      setRepos(res.data?.repositories ?? []);
    } catch (e) {
      setFetchError(toErr(e, 'Failed to fetch repositories'));
    } finally {
      setReposLoading(false);
    }
  }

  async function handleSelectRepo(name: string) {
    if (!name || name === selectedRepo) return;
    setSelectedRepo(name);
    resetBelowRepo();
    setBranchesLoading(true);
    setWorkflowsLoading(true);
    setEnvLoading(true);
    try {
      const [branchRes, workflowRes, envRes] = await Promise.all([
        getGithubBranches(provider, pat, selectedOrg, name),
        getGithubWorkflows(provider, pat, selectedOrg, name),
        getGithubEnvironments(provider, pat, selectedOrg, name),
      ]);
      setBranches(branchRes.data?.branches ?? []);
      setWorkflows(workflowRes.data?.workflows ?? []);
      setEnvironments(envRes.data?.environments ?? []);
      // Preselect the default branch when available.
      const def = branchRes.data?.defaultBranch;
      if (def && (branchRes.data?.branches ?? []).some(b => b.name === def)) {
        setSelectedBranch(def);
      }
    } catch (e) {
      setFetchError(toErr(e, 'Failed to load repository details'));
    } finally {
      setBranchesLoading(false);
      setWorkflowsLoading(false);
      setEnvLoading(false);
    }
  }

  async function handleSelectBranch(name: string) {
    if (!name) return;
    setSelectedBranch(name);
    setDeployment(null);
    // Re-resolve workflow jobs against the newly selected branch.
    if (selectedWorkflow) {
      await loadJobs(selectedWorkflow, name);
    }
  }

  async function loadJobs(workflowId: string, branch: string) {
    setJobsLoading(true);
    setJobs([]);
    setSelectedJob('');
    try {
      const res = await getGithubWorkflowJobs(
        provider,
        pat,
        selectedOrg,
        selectedRepo,
        workflowId,
        branch,
      );
      setJobs(res.data?.jobs ?? []);
    } catch (e) {
      setFetchError(toErr(e, 'Failed to fetch workflow jobs'));
    } finally {
      setJobsLoading(false);
    }
  }

  async function handleSelectWorkflow(workflowId: string) {
    if (!workflowId) return;
    setSelectedWorkflow(workflowId);
    setSelectedJob('');
    await loadJobs(workflowId, selectedBranch);
  }

  async function handleSelectEnvironment(name: string) {
    setSelectedEnvironment(name);
    await loadLatestDeployment(name);
  }

  async function loadLatestDeployment(environment?: string) {
    if (!selectedRepo || !selectedBranch) return;
    setDeploymentLoading(true);
    setDeployment(null);
    try {
      const res = await getGithubLatestDeployment(
        provider,
        pat,
        selectedOrg,
        selectedRepo,
        {
          environment: environment || undefined,
          branch: selectedBranch,
          workflowId: selectedWorkflow || undefined,
        },
      );
      setDeployment(res.data?.deployment ?? null);
    } catch {
      // Non-fatal: commit is auto-resolved server-side during detect.
      setDeployment(null);
    } finally {
      setDeploymentLoading(false);
    }
  }

  // ── Test cascade handlers ─────────────────────────────────────────────────

  async function handleFetchTestOrganizations() {
    if (!testPat || testPat.length < 5) return;
    setTestOrgLoading(true);
    resetTestCascade();
    setTestOrgs([]);
    try {
      const res = await getGithubOrganizations(provider, testPat);
      setTestOrgs(res.data?.organizations ?? []);
    } catch (e) {
      setScriptsError(toErr(e, 'Failed to fetch test organizations'));
    } finally {
      setTestOrgLoading(false);
    }
  }

  async function handleSelectTestOrg(login: string) {
    if (!login) return;
    setSelectedTestOrg(login);
    setSelectedTestRepo('');
    setSelectedTestBranch('');
    setSelectedTestWorkflow('');
    setSelectedTestJob('');
    setTestRepos([]);
    setTestBranches([]);
    setTestWorkflows([]);
    setTestJobs([]);
    resetScriptState();
    setTestReposLoading(true);
    try {
      const res = await getGithubRepositories(provider, testPat, login);
      setTestRepos(res.data?.repositories ?? []);
    } catch (e) {
      setScriptsError(toErr(e, 'Failed to fetch test repositories'));
    } finally {
      setTestReposLoading(false);
    }
  }

  async function handleSelectTestRepo(name: string) {
    if (!name) return;
    setSelectedTestRepo(name);
    setSelectedTestBranch('');
    setSelectedTestWorkflow('');
    setSelectedTestJob('');
    setTestPipelinePlatform('');
    setJenkinsJobName('');
    setJenkinsJobs([]);
    setJenkinsJobsError(null);
    setTestBranches([]);
    setTestWorkflows([]);
    setTestJobs([]);
    resetScriptState();
    setTestBranchesLoading(true);
    setTestWorkflowsLoading(true);
    try {
      const [branchRes, workflowRes] = await Promise.all([
        getGithubBranches(provider, testPat, selectedTestOrg, name),
        getGithubWorkflows(provider, testPat, selectedTestOrg, name),
      ]);
      setTestBranches(branchRes.data?.branches ?? []);
      setTestWorkflows(workflowRes.data?.workflows ?? []);
    } catch (e) {
      setScriptsError(toErr(e, 'Failed to load test repository details'));
    } finally {
      setTestBranchesLoading(false);
      setTestWorkflowsLoading(false);
    }
  }

  function handleSelectTestPipelinePlatform(platform: string) {
    const next = platform as PipelinePlatform | '';
    setTestPipelinePlatform(next);
    setSelectedTestWorkflow('');
    setSelectedTestJob('');
    setTestJobs([]);
    setJenkinsJobName('');
    setJenkinsJobs([]);
    setJenkinsJobsError(null);
  }

  async function handleFetchJenkinsJobs() {
    setJenkinsJobsError(null);
    setJenkinsJobs([]);
    setJenkinsJobName('');
    if (
      !jenkinsBaseUrl.trim() ||
      !jenkinsUsername.trim() ||
      !jenkinsApiToken.trim()
    ) {
      setJenkinsJobsError('Jenkins URL, username, and API token are required.');
      return;
    }
    setJenkinsJobsLoading(true);
    try {
      const res = await fetchJenkinsJobs({
        baseUrl: jenkinsBaseUrl,
        username: jenkinsUsername,
        apiToken: jenkinsApiToken,
      });
      const jobs = res.data?.jobs ?? [];
      setJenkinsJobs(jobs);
      if (jobs.length === 0) {
        setJenkinsJobsError('No Jenkins jobs found for these credentials.');
      }
    } catch (e) {
      setJenkinsJobsError(toErr(e, 'Failed to fetch Jenkins jobs'));
    } finally {
      setJenkinsJobsLoading(false);
    }
  }

  async function handleSelectTestWorkflow(workflowId: string) {
    if (!workflowId) return;
    setSelectedTestWorkflow(workflowId);
    setSelectedTestJob('');
    setTestJobsLoading(true);
    setTestJobs([]);
    try {
      const res = await getGithubWorkflowJobs(
        provider,
        testPat,
        selectedTestOrg,
        selectedTestRepo,
        workflowId,
        selectedTestBranch,
      );
      setTestJobs(res.data?.jobs ?? []);
    } catch (e) {
      setScriptsError(toErr(e, 'Failed to fetch test workflow jobs'));
    } finally {
      setTestJobsLoading(false);
    }
  }

  async function handleLoadTestScripts() {
    if (!selectedTestRepo || !selectedTestBranch) {
      setScriptsError('Select a test repository and branch first.');
      return;
    }
    setScriptsLoading(true);
    setScriptsError(null);
    setScriptsLoaded(false);
    try {
      const res = await getGithubTestScripts(
        provider,
        testPat,
        selectedTestOrg,
        selectedTestRepo,
        selectedTestBranch,
        { workflowId: selectedTestWorkflow || undefined },
      );
      if (!res.success) {
        setScriptsError(res.message || 'Failed to load test scripts');
        setScriptCandidates([]);
        setSelectedScriptPaths(new Set());
        return;
      }
      const candidates = res.data?.candidates ?? [];
      setScriptCandidates(candidates);
      setSelectedScriptPaths(
        new Set(candidates.filter(c => c.selectedByDefault).map(c => c.filePath)),
      );
      setScriptsLoaded(true);
    } catch (e) {
      setScriptsError(toErr(e, 'Failed to load test scripts'));
      setScriptCandidates([]);
      setSelectedScriptPaths(new Set());
    } finally {
      setScriptsLoading(false);
    }
  }

  // ── Detect / onboard ──────────────────────────────────────────────────────

  async function validateCaptureFrontendUrl(
    url: string,
    enabled: boolean = captureBeforeAfter,
  ) {
    if (!enabled) {
      setCaptureUrlError(null);
      setCaptureUrlOk(false);
      return true;
    }
    const trimmed = url.trim();
    if (!trimmed) {
      setCaptureUrlError(
        'Unable to access this URL. Please verify the application is running and accessible.',
      );
      setCaptureUrlOk(false);
      return false;
    }
    setCaptureValidating(true);
    try {
      const res = await validateCaptureUrl(trimmed);
      if (!res.ok) {
        setCaptureUrlError(
          res.error ||
            'Unable to access this URL. Please verify the application is running and accessible.',
        );
        setCaptureUrlOk(false);
        return false;
      }
      setCaptureUrlError(null);
      setCaptureUrlOk(true);
      return true;
    } catch {
      setCaptureUrlError(
        'Unable to access this URL. Please verify the application is running and accessible.',
      );
      setCaptureUrlOk(false);
      return false;
    } finally {
      setCaptureValidating(false);
    }
  }

  function scheduleCaptureUrlValidation(url: string, enabled: boolean) {
    if (captureValidateTimer.current) {
      clearTimeout(captureValidateTimer.current);
      captureValidateTimer.current = null;
    }
    if (!enabled) {
      setCaptureUrlError(null);
      setCaptureUrlOk(false);
      return;
    }
    if (!url.trim()) {
      setCaptureUrlOk(false);
      setCaptureUrlError(null);
      return;
    }
    captureValidateTimer.current = setTimeout(() => {
      void validateCaptureFrontendUrl(url, true);
    }, 500);
  }

  async function handleDetectRepository() {
    setDetectError(null);
    const urlValidation = validateDeploymentUrls(frontendUrl, backendUrl);
    if (urlValidation) {
      setUrlErrors(urlValidation);
      return;
    }
    setUrlErrors({});
    // Capture URL must already be validated when the checkbox is on
    // (validated on URL entry / checkbox select — do not re-run Playwright here).
    if (captureBeforeAfter) {
      if (captureUrlError || !captureUrlOk) {
        setCaptureUrlError(
          captureUrlError ||
            'Unable to access this URL. Please verify the application is running and accessible.',
        );
        return;
      }
    }
    if (!selectedRepoObj) {
      setDetectError('Select a repository first.');
      return;
    }
    if (testEnabled) {
      if (!selectedTestRepo || !selectedTestBranch || !testPipelinePlatform) {
        setScriptsError(
          'Complete test repository, branch, and pipeline platform fields.',
        );
        return;
      }
      if (testPipelinePlatform === 'ado') {
        setScriptsError(
          'Azure Pipelines CI is configured from Azure DevOps onboarding. Choose GitHub Actions or Jenkins here.',
        );
        return;
      }
      if (
        testPipelinePlatform === 'github-actions' &&
        (!selectedTestWorkflow || !selectedTestJob)
      ) {
        setScriptsError('Select a test workflow and workflow job.');
        return;
      }
      if (
        testPipelinePlatform === 'jenkins' &&
        (!jenkinsBaseUrl.trim() ||
          !jenkinsUsername.trim() ||
          !jenkinsApiToken.trim() ||
          !jenkinsJobName.trim())
      ) {
        setScriptsError('Complete all Jenkins fields and select a job.');
        return;
      }
      if (!scriptsLoaded || selectedScriptPaths.size === 0) {
        setScriptsError(
          scriptsLoaded
            ? 'Select at least one test script before Detect Repository.'
            : 'Load test scripts and select files before Detect Repository.',
        );
        return;
      }
    }

    const repoUrl =
      selectedRepoObj.htmlUrl ||
      `https://github.com/${selectedOrg}/${selectedRepo}`;

    const selectedJenkinsJob =
      jenkinsJobs.find(j => j.fullName === jenkinsJobName) ?? null;

    const body: GithubDetectBody = {
      organization: selectedOrg,
      repository: selectedRepo,
      repoUrl,
      branch: selectedBranch,
      commitId: deployment?.sha,
      workflowId: selectedWorkflow || undefined,
      workflowName: selectedWorkflowObj?.name,
      workflowJob: selectedJob || undefined,
      environment: selectedEnvironment || undefined,
      onboardedBy: ONBOARDED_BY,
      frontendUrl: frontendUrl.trim() || undefined,
      backendUrl: backendUrl.trim() || undefined,
      codeCredentials: { pat },
      testAutomation: testEnabled
        ? {
            location: 'different',
            testOrganization: selectedTestOrg,
            testRepository: selectedTestRepo,
            testRepoId:
              selectedTestRepoObj?.id != null
                ? String(selectedTestRepoObj.id)
                : undefined,
            testRepoUrl:
              selectedTestRepoObj?.htmlUrl ||
              `https://github.com/${selectedTestOrg}/${selectedTestRepo}`,
            testBranchName: selectedTestBranch,
            testWorkflowId:
              testPipelinePlatform === 'github-actions'
                ? selectedTestWorkflow || undefined
                : undefined,
            testWorkflowName:
              testPipelinePlatform === 'github-actions'
                ? selectedTestWorkflowObj?.name
                : undefined,
            testWorkflowJob:
              testPipelinePlatform === 'github-actions'
                ? selectedTestJob || undefined
                : undefined,
            selectedScriptPaths: Array.from(selectedScriptPaths),
            testPat,
            pipelinePlatform: testPipelinePlatform || 'github-actions',
            ...(testPipelinePlatform === 'jenkins'
              ? {
                  jenkins: {
                    baseUrl: jenkinsBaseUrl.trim(),
                    username: jenkinsUsername.trim(),
                    apiToken: jenkinsApiToken.trim(),
                    jobName: jenkinsJobName.trim(),
                    jobUrl: selectedJenkinsJob?.url,
                  },
                }
              : {}),
          }
        : {
            location: 'same',
            testPat: pat,
            pipelinePlatform: 'github-actions',
            testRepository: selectedRepo,
            testRepoId:
              selectedRepoObj?.id != null ? String(selectedRepoObj.id) : undefined,
            testRepoUrl:
              selectedRepoObj?.htmlUrl ||
              `https://github.com/${selectedOrg}/${selectedRepo}`,
            testBranchName: selectedBranch,
          },
    };

    setDetectLoading(true);
    try {
      const res = await detectGithubRepository(provider, body, pat);
      const newRepoId = res.onboardData?.repositoryId ?? '';
      setDbRepoId(newRepoId);
      setDbBranchId(res.onboardData?.branchId ?? '');
      const files = res.repoFiles?.repoResponse?.files ?? [];
      const analysis = filterNonTestFiles(files);
      setAnalysisResult(analysis);
      setSelectedFiles(
        new Set([...analysis.nonTestFiles, ...analysis.nonTestlargerFiles]),
      );

      if (captureBeforeAfter && newRepoId && frontendUrl.trim()) {
        try {
          await enableRepositoryCapture(newRepoId, {
            frontendUrl: frontendUrl.trim(),
            enabled: true,
            optedInBy: ONBOARDED_BY,
            branch: selectedBranch,
            repositoryName: selectedRepo,
          });
        } catch (capErr) {
          console.warn('Before capture queue failed', capErr);
        }
      }

      if (testEnabled) {
        const idx = res.testScriptIndex;
        if (idx?.status === 'processing') {
          setScriptIndexStatus('processing');
          setScriptIndexMessage(
            idx.message ?? 'Indexing test scripts in background…',
          );
          setScriptIndexProgress({
            processed: 0,
            total: idx.selectedCount ?? selectedScriptPaths.size,
            percentage: 0,
            phase: 'hashing',
          });
        } else if (idx?.status === 'completed') {
          setScriptIndexStatus('completed');
          setScriptIndexMessage(idx.message ?? null);
        } else if (idx?.status === 'failed') {
          setScriptIndexStatus('failed');
          setScriptIndexMessage(idx.message ?? 'Test script indexing failed');
        } else if (idx?.status === 'skipped') {
          setScriptIndexStatus('skipped');
          setScriptIndexMessage(idx.message ?? null);
        } else {
          setScriptIndexStatus('idle');
          setScriptIndexMessage(null);
        }
      }

      setShowPostDetect(true);
    } catch (e) {
      setDetectError(toErr(e, 'Detection failed'));
    } finally {
      setDetectLoading(false);
    }
  }

  // ── Reset helpers ───────────────────────────────────────────────────────

  function resetBelowOrg() {
    setSelectedRepo('');
    setSelectedBranch('');
    setSelectedWorkflow('');
    setSelectedJob('');
    setSelectedEnvironment('');
    setRepos([]);
    setBranches([]);
    setWorkflows([]);
    setJobs([]);
    setEnvironments([]);
    setDeployment(null);
  }

  function resetBelowRepo() {
    setSelectedBranch('');
    setSelectedWorkflow('');
    setSelectedJob('');
    setSelectedEnvironment('');
    setBranches([]);
    setWorkflows([]);
    setJobs([]);
    setEnvironments([]);
    setDeployment(null);
  }

  function resetApplicationCascade() {
    setSelectedOrg('');
    resetBelowOrg();
  }

  function resetScriptState() {
    setScriptCandidates([]);
    setSelectedScriptPaths(new Set());
    setScriptsLoaded(false);
    setScriptsError(null);
    setScriptIndexStatus('idle');
    setScriptIndexMessage(null);
    setScriptIndexProgress(EMPTY_SCRIPT_INDEX_PROGRESS);
  }

  function resetTestCascade() {
    setSelectedTestOrg('');
    setSelectedTestRepo('');
    setSelectedTestBranch('');
    setSelectedTestWorkflow('');
    setSelectedTestJob('');
    setTestRepos([]);
    setTestBranches([]);
    setTestWorkflows([]);
    setTestJobs([]);
    resetScriptState();
  }

  function handleResetAll() {
    setPat('');
    setOrgs([]);
    resetApplicationCascade();
    setTestEnabled(false);
    setTestPat('');
    setTestOrgs([]);
    resetTestCascade();
    setDbRepoId('');
    setDbBranchId('');
    setAnalysisResult(null);
    setSelectedFiles(new Set());
    setShowPostDetect(false);
    setDetectError(null);
    setFetchError(null);
    setPrEmails([]);
    setAutoPrEmails([]);
    setSkipPrEmails(false);
    setSkipAutoPrEmails(false);
    setPrEmailsError('');
    setAutoPrEmailsError('');
    setFrontendUrl('');
    setBackendUrl('');
    setUrlErrors({});
  }

  // ── Derived UI ────────────────────────────────────────────────────────────

  const isPostDetect = showPostDetect;

  const urlsReady =
    Boolean(frontendUrl.trim() || backendUrl.trim()) &&
    isValidOptionalUrl(frontendUrl) &&
    isValidOptionalUrl(backendUrl);

  const canDetect =
    Boolean(pat) &&
    Boolean(selectedOrg) &&
    Boolean(selectedRepo) &&
    Boolean(selectedBranch) &&
    Boolean(selectedWorkflow) &&
    Boolean(selectedJob) &&
    (environments.length === 0 || Boolean(selectedEnvironment)) &&
    urlsReady &&
    !isPostDetect;

  const canLoadTestScripts =
    Boolean(selectedTestOrg) &&
    Boolean(selectedTestRepo) &&
    Boolean(selectedTestBranch) &&
    (testPipelinePlatform === 'jenkins' ||
      (testPipelinePlatform === 'github-actions' &&
        Boolean(selectedTestWorkflow) &&
        Boolean(selectedTestJob)));

  const onboardStatus = detectLoading
    ? 'Connecting…'
    : showPostDetect
      ? 'Connected'
      : orgLoading
        ? 'Fetching…'
        : orgs.length > 0
          ? 'Select Options'
          : 'Ready';

  const statusBadgeType: 'success' | 'warning' | 'info' | 'danger' =
    onboardStatus === 'Connected'
      ? 'success'
      : onboardStatus === 'Fetching…' || onboardStatus === 'Connecting…'
        ? 'warning'
        : 'info';

  const orgOptions: Option[] = orgs.map(o => ({
    value: o.login,
    label: o.type === 'User' ? `${o.login} (you)` : o.login,
  }));
  const repoOptions: Option[] = repos.map(r => ({
    value: r.name,
    label: r.name,
  }));
  const branchOptions: Option[] = branches.map(b => ({
    value: b.name,
    label: b.isDefault ? `${b.name} (default)` : b.name,
  }));
  const workflowOptions: Option[] = workflows.map(w => ({
    value: String(w.id),
    label: w.name,
  }));
  const jobOptions: Option[] = jobs.map(j => ({ value: j.name, label: j.name }));
  const envOptions: Option[] = environments.map(e => ({
    value: e.name,
    label: e.name,
  }));
  const testOrgOptions: Option[] = testOrgs.map(o => ({
    value: o.login,
    label: o.type === 'User' ? `${o.login} (you)` : o.login,
  }));
  const testRepoOptions: Option[] = testRepos.map(r => ({
    value: r.name,
    label: r.name,
  }));
  const testBranchOptions: Option[] = testBranches.map(b => ({
    value: b.name,
    label: b.isDefault ? `${b.name} (default)` : b.name,
  }));
  const testWorkflowOptions: Option[] = testWorkflows.map(w => ({
    value: String(w.id),
    label: w.name,
  }));
  const testJobOptions: Option[] = testJobs.map(j => ({
    value: j.name,
    label: j.name,
  }));

  const deploymentBoxClass =
    'mt-2 flex flex-wrap items-center gap-x-4 gap-y-2 rounded-xl border border-solid border-[var(--inv-border)] bg-[var(--inv-surface-muted)] px-4 py-3 text-[0.8125rem]';

  const deploymentBox: ReactNode = deploymentLoading ? (
    <div className={deploymentBoxClass}>
      <Spinner compact />
      <span>Resolving latest deployment…</span>
    </div>
  ) : deployment?.sha ? (
    <div className={deploymentBoxClass}>
      <span className="font-semibold text-[var(--inv-text)]">
        Latest successful deployment
      </span>
      <span className="font-mono text-[var(--inv-text-body,#4f453c)]">
        {deployment.sha.slice(0, 10)}
      </span>
      {deployment.environment && (
        <StatusBadge value={deployment.environment} variant="info-outlined" />
      )}
      {deployment.state && (
        <StatusBadge value={deployment.state} variant="info-outlined" />
      )}
      <span>via {deployment.source.replace('_', ' ')}</span>
    </div>
  ) : selectedBranch ? (
    <p className="mt-2 text-[0.8125rem] text-[var(--inv-text-secondary)]">
      No prior deployment found. The latest commit on{' '}
      <strong className="font-semibold text-[var(--inv-text)]">
        {selectedBranch}
      </strong>{' '}
      will be used automatically.
    </p>
  ) : null;

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <OnboardBgWrapper>
      <OnboardingPage>
        <ChangePlatformButton onClick={onChangeProvider} />
        <OnboardingPageHeader
          eyebrow="Onboarding"
          title="Onboard from GitHub"
          subtitle="Connect your GitHub repository, workflow, and automation pipeline to start scanning."
        />

          <OnboardCard>
            <OnboardCardHeader
              title="Application Repository"
              action={
                <StatusBadge value={onboardStatus} variant={statusBadgeType} />
              }
            />

            <OnboardCardBody>
              <PatRow
                action={
                  <Button
                    type="button"
                    className="h-10 shrink-0 whitespace-nowrap"
                    onClick={() => void handleFetchOrganizations()}
                    disabled={
                      isPostDetect || orgLoading || !pat || pat.length < 5
                    }>
                    {orgLoading ? 'Fetching…' : 'Fetch Organizations'}
                  </Button>
                }>
                <FormField label="Personal Access Token (PAT)" layout="pat">
                  <PasswordInput
                    title="GitHub PAT"
                    placeholder="Enter your GitHub PAT"
                    value={pat}
                    onChange={setPat}
                    disabled={isPostDetect}
                  />
                </FormField>
              </PatRow>

              {orgs.length > 0 && (
                <CascadeSection locked={isPostDetect}>
                  <FormRow cols={2}>
                    <SelectField
                      label="Organization"
                      value={selectedOrg}
                      options={orgOptions}
                      onSelect={val => void handleSelectOrg(val)}
                      disabled={isPostDetect || orgs.length === 0}
                      placeholder="Select Organization"
                    />
                    <SelectField
                      label="Repository"
                      value={selectedRepo}
                      options={repoOptions}
                      onSelect={val => void handleSelectRepo(val)}
                      disabled={isPostDetect || !selectedOrg}
                      loading={reposLoading}
                      placeholder={
                        !selectedOrg ? 'Select an organization first' : 'Select Repository'
                      }
                    />
                  </FormRow>
                  <FormRow cols={3}>
                    <SelectField
                      label="Branch"
                      value={selectedBranch}
                      options={branchOptions}
                      onSelect={val => void handleSelectBranch(val)}
                      disabled={isPostDetect || !selectedRepo}
                      loading={branchesLoading}
                      placeholder={
                        !selectedRepo ? 'Select a repository first' : 'Select Branch'
                      }
                    />
                    <SelectField
                      label="Workflow"
                      value={selectedWorkflow}
                      options={workflowOptions}
                      onSelect={val => void handleSelectWorkflow(val)}
                      disabled={isPostDetect || !selectedRepo}
                      loading={workflowsLoading}
                      placeholder={
                        !selectedRepo ? 'Select a repository first' : 'Select Workflow'
                      }
                    />
                    <SelectField
                      label="Workflow Job"
                      value={selectedJob}
                      options={jobOptions}
                      onSelect={val => setSelectedJob(val)}
                      disabled={isPostDetect || !selectedWorkflow}
                      loading={jobsLoading}
                      placeholder={
                        !selectedWorkflow ? 'Select a workflow first' : 'Select Workflow Job'
                      }
                    />
                  </FormRow>
                  <FormRow cols={2}>
                    <SelectField
                      label="Environment"
                      value={selectedEnvironment}
                      options={envOptions}
                      onSelect={val => void handleSelectEnvironment(val)}
                      disabled={isPostDetect || !selectedRepo || environments.length === 0}
                      loading={envLoading}
                      placeholder={
                        environments.length === 0
                          ? 'No environments defined'
                          : 'Select Environment'
                      }
                    />
                    <FormField label="Latest successful deployment">
                      {deploymentBox ?? (
                        <p className="mt-1.5 text-xs text-[var(--inv-text-muted)]">
                          Auto-resolved after selecting an environment or branch.
                        </p>
                      )}
                    </FormField>
                  </FormRow>
                  <FormRow cols={2}>
                    <FormField
                      label="Frontend URL"
                      error={urlErrors.frontendUrl || captureUrlError || undefined}>
                      <Input
                        type="url"
                        placeholder="e.g. https://app.example.com"
                        value={frontendUrl}
                        onChange={e => {
                          const next = e.target.value;
                          setFrontendUrl(next);
                          setCaptureUrlOk(false);
                          if (urlErrors.frontendUrl || urlErrors.backendUrl) {
                            setUrlErrors(
                              validateDeploymentUrls(next, backendUrl) ?? {},
                            );
                          }
                          if (captureBeforeAfter) {
                            scheduleCaptureUrlValidation(next, true);
                          } else {
                            setCaptureUrlError(null);
                          }
                        }}
                        onBlur={() => {
                          if (captureBeforeAfter && frontendUrl.trim()) {
                            if (captureValidateTimer.current) {
                              clearTimeout(captureValidateTimer.current);
                              captureValidateTimer.current = null;
                            }
                            void validateCaptureFrontendUrl(frontendUrl, true);
                          }
                        }}
                        disabled={isPostDetect}
                      />
                    </FormField>
                    <FormField label="Backend URL" error={urlErrors.backendUrl}>
                      <Input
                        type="url"
                        placeholder="e.g. https://api.example.com"
                        value={backendUrl}
                        onChange={e => {
                          setBackendUrl(e.target.value);
                          if (urlErrors.frontendUrl || urlErrors.backendUrl) {
                            setUrlErrors(
                              validateDeploymentUrls(
                                frontendUrl,
                                e.target.value,
                              ) ?? {},
                            );
                          }
                        }}
                        disabled={isPostDetect}
                      />
                    </FormField>
                  </FormRow>
                  <div className="mt-3 flex items-start gap-2">
                    <input
                      id="capture-before-after"
                      type="checkbox"
                      className="mt-1 h-4 w-4"
                      checked={captureBeforeAfter}
                      disabled={isPostDetect || captureValidating}
                      onChange={e => {
                        const checked = e.target.checked;
                        setCaptureBeforeAfter(checked);
                        setCaptureUrlError(null);
                        setCaptureUrlOk(false);
                        if (checked && frontendUrl.trim()) {
                          // Pass enabled=true — state update is async/stale otherwise
                          void validateCaptureFrontendUrl(frontendUrl, true);
                        } else if (captureValidateTimer.current) {
                          clearTimeout(captureValidateTimer.current);
                          captureValidateTimer.current = null;
                        }
                      }}
                    />
                    <label
                      htmlFor="capture-before-after"
                      className="text-sm text-[var(--inv-text-secondary)]">
                      Capture Before &amp; After Instrumentation
                      {captureValidating ? (
                        <span className="ml-2 text-xs text-[var(--inv-text-muted)]">
                          Validating URL…
                        </span>
                      ) : null}
                      {captureUrlOk && captureBeforeAfter ? (
                        <span className="ml-2 text-xs text-emerald-600">
                          URL verified
                        </span>
                      ) : null}
                    </label>
                  </div>
                </CascadeSection>
              )}

              {orgs.length > 0 && !testEnabled && !isPostDetect && (
                <div className="mt-8 flex">
                  <Button
                    variant="secondary"
                    type="button"
                    onClick={() => setTestEnabled(true)}>
                    Connect Test Repository
                  </Button>
                </div>
              )}

              {orgs.length > 0 && testEnabled && (
                <TestSection>
                  <SectionDivider label="Test Repository" />
                  <PatRow
                    action={
                      <Button
                        type="button"
                        className="h-10 shrink-0 whitespace-nowrap"
                        onClick={() => void handleFetchTestOrganizations()}
                        disabled={
                          isPostDetect ||
                          testOrgLoading ||
                          !testPat ||
                          testPat.length < 5
                        }>
                        {testOrgLoading
                          ? 'Fetching…'
                          : 'Fetch Test Organizations'}
                      </Button>
                    }>
                    <FormField
                      label="Personal Access Token (Test)"
                      layout="pat">
                      <PasswordInput
                        title="GitHub Test PAT"
                        placeholder="Enter your test GitHub PAT"
                        value={testPat}
                        onChange={setTestPat}
                        disabled={isPostDetect}
                      />
                    </FormField>
                  </PatRow>

                  {testOrgs.length > 0 && (
                    <>
                      <FormRow cols={2}>
                        <SelectField
                          label="Test Organization"
                          value={selectedTestOrg}
                          options={testOrgOptions}
                          onSelect={val => void handleSelectTestOrg(val)}
                          disabled={isPostDetect}
                          placeholder="Select Test Organization"
                        />
                        <SelectField
                          label="Test Repository"
                          value={selectedTestRepo}
                          options={testRepoOptions}
                          onSelect={val => void handleSelectTestRepo(val)}
                          disabled={isPostDetect || !selectedTestOrg}
                          loading={testReposLoading}
                          placeholder={
                            !selectedTestOrg
                              ? 'Select a test organization first'
                              : 'Select Test Repository'
                          }
                        />
                      </FormRow>
                      <FormRow cols={2}>
                        <SelectField
                          label="Test Branch"
                          value={selectedTestBranch}
                          options={testBranchOptions}
                          onSelect={val => {
                            setSelectedTestBranch(val);
                            setTestPipelinePlatform('');
                            setSelectedTestWorkflow('');
                            setSelectedTestJob('');
                            setJenkinsJobName('');
                            setJenkinsJobs([]);
                            setJenkinsJobsError(null);
                          }}
                          disabled={isPostDetect || !selectedTestRepo}
                          loading={testBranchesLoading}
                          placeholder={
                            !selectedTestRepo
                              ? 'Select a test repository first'
                              : 'Select Test Branch'
                          }
                        />
                        <SelectField
                          label="Pipeline Platform"
                          value={testPipelinePlatform}
                          options={PIPELINE_PLATFORM_OPTIONS.map(o => ({
                            value: o.value,
                            label: o.label,
                          }))}
                          onSelect={handleSelectTestPipelinePlatform}
                          disabled={isPostDetect || !selectedTestBranch}
                          placeholder={
                            !selectedTestBranch
                              ? 'Select a test branch first'
                              : 'Select Pipeline Platform'
                          }
                        />
                      </FormRow>

                      {testPipelinePlatform === 'github-actions' && (
                        <FormRow cols={2}>
                          <SelectField
                            label="Test Workflow"
                            value={selectedTestWorkflow}
                            options={testWorkflowOptions}
                            onSelect={val => void handleSelectTestWorkflow(val)}
                            disabled={isPostDetect || !selectedTestRepo}
                            loading={testWorkflowsLoading}
                            placeholder={
                              !selectedTestRepo
                                ? 'Select a test repository first'
                                : 'Select Test Workflow'
                            }
                          />
                          <SelectField
                            label="Test Workflow Job"
                            value={selectedTestJob}
                            options={testJobOptions}
                            onSelect={val => setSelectedTestJob(val)}
                            disabled={isPostDetect || !selectedTestWorkflow}
                            loading={testJobsLoading}
                            placeholder={
                              !selectedTestWorkflow
                                ? 'Select a test workflow first'
                                : 'Select Test Workflow Job'
                            }
                          />
                        </FormRow>
                      )}

                      {testPipelinePlatform === 'ado' && (
                        <p className="m-0 text-sm text-[var(--inv-text-secondary)]">
                          Azure Pipelines CI is configured from Azure DevOps
                          onboarding. Choose GitHub Actions or Jenkins for this
                          GitHub test repository.
                        </p>
                      )}

                      {testPipelinePlatform === 'jenkins' && (
                        <>
                          <FormRow cols={3}>
                            <FormField label="Jenkins URL">
                              <Input
                                value={jenkinsBaseUrl}
                                onChange={e =>
                                  setJenkinsBaseUrl(e.target.value)
                                }
                                placeholder="e.g. http://13.205.65.111:8080"
                                disabled={isPostDetect}
                              />
                            </FormField>
                            <FormField label="Jenkins Username">
                              <Input
                                value={jenkinsUsername}
                                onChange={e =>
                                  setJenkinsUsername(e.target.value)
                                }
                                placeholder="Jenkins login username"
                                disabled={isPostDetect}
                              />
                            </FormField>
                            <FormField label="Jenkins API Token">
                              <PasswordInput
                                title="Jenkins API Token"
                                placeholder="API token for this user"
                                value={jenkinsApiToken}
                                onChange={setJenkinsApiToken}
                                disabled={isPostDetect}
                              />
                            </FormField>
                          </FormRow>
                          <div className="mt-2 flex flex-wrap items-center gap-3">
                            <Button
                              type="button"
                              variant="secondary"
                              disabled={
                                isPostDetect ||
                                jenkinsJobsLoading ||
                                !jenkinsBaseUrl.trim() ||
                                !jenkinsUsername.trim() ||
                                !jenkinsApiToken.trim()
                              }
                              onClick={() => void handleFetchJenkinsJobs()}>
                              {jenkinsJobsLoading
                                ? 'Fetching jobs…'
                                : 'Fetch Jenkins Jobs'}
                            </Button>
                          </div>
                          <ApiErrorAlert message={jenkinsJobsError} />
                          <FormRow cols={2}>
                            <SelectField
                              label="Jenkins Job"
                              value={jenkinsJobName}
                              options={jenkinsJobs.map(j => ({
                                value: j.fullName,
                                label: j.fullName,
                              }))}
                              onSelect={setJenkinsJobName}
                              disabled={
                                isPostDetect || jenkinsJobs.length === 0
                              }
                              loading={jenkinsJobsLoading}
                              placeholder={
                                jenkinsJobs.length === 0
                                  ? 'Fetch Jenkins jobs first'
                                  : 'Select Jenkins Job'
                              }
                            />
                          </FormRow>
                        </>
                      )}

                      {!isPostDetect && (
                        <TestScriptLoadSection>
                          <Button
                            variant="secondary"
                            type="button"
                            disabled={scriptsLoading || !canLoadTestScripts}
                            onClick={() => void handleLoadTestScripts()}>
                            {scriptsLoading
                              ? 'Loading test scripts…'
                              : 'Load test scripts'}
                          </Button>
                          {!scriptsLoaded && !scriptsLoading && (
                            <p className="my-4 text-sm text-[var(--inv-text-secondary)]">
                              Parses the selected workflow and repository to
                              discover Playwright test scripts.
                            </p>
                          )}
                          <ApiErrorAlert message={scriptsError} />
                          {(scriptsLoaded || scriptsLoading) && (
                            <TestScriptSelectionCard
                              candidates={scriptCandidates}
                              selectedPaths={selectedScriptPaths}
                              onSelectionChange={setSelectedScriptPaths}
                              loading={scriptsLoading}
                              error={null}
                              unresolvedCount={0}
                            />
                          )}
                        </TestScriptLoadSection>
                      )}
                    </>
                  )}
                </TestSection>
              )}

              {showPostDetect && analysisResult && (
                <TechStackSection>
                  <InfoAlert>Repository connected. Tech stack detected.</InfoAlert>
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-sm font-medium text-[var(--inv-text-secondary)]">
                      Detected Language:
                    </span>
                    {analysisResult.isJavaScript && (
                      <StatusBadge value="JavaScript" variant="info-outlined" />
                    )}
                    {analysisResult.isTypeScript && (
                      <StatusBadge value="TypeScript" variant="info-outlined" />
                    )}
                    {analysisResult.isMixed && (
                      <StatusBadge
                        value="JavaScript / TypeScript"
                        variant="info-outlined"
                      />
                    )}
                    {analysisResult.isCSharp && (
                      <StatusBadge value="C#" variant="info-outlined" />
                    )}
                    {analysisResult.isPython && (
                      <StatusBadge value="Python" variant="info-outlined" />
                    )}
                    {analysisResult.isJava && (
                      <StatusBadge value="Java" variant="info-outlined" />
                    )}
                    {!analysisResult.isJavaScript &&
                      !analysisResult.isTypeScript &&
                      !analysisResult.isMixed &&
                      !analysisResult.isCSharp &&
                      !analysisResult.isPython &&
                      !analysisResult.isJava && (
                        <StatusBadge value="Other" variant="info-outlined" />
                      )}
                  </div>
                </TechStackSection>
              )}

              <ApiErrorAlert message={fetchError} />
              <ApiErrorAlert message={detectError} />

              <FooterButtons
                left={
                  <>
                    {!isPostDetect && (
                      <Button
                        type="button"
                        disabled={!canDetect || detectLoading}
                        onClick={() => void handleDetectRepository()}>
                        {detectLoading ? 'Detecting…' : 'Detect Repository'}
                      </Button>
                    )}
                    {detectLoading && <Spinner compact />}
                  </>
                }
                right={
                  <Button
                    variant="outline"
                    type="button"
                    onClick={handleResetAll}>
                    Reset
                  </Button>
                }
              />
            </OnboardCardBody>
          </OnboardCard>

          {/* Post-detect: file exclusions + scan */}
          {showPostDetect && analysisResult && (
            <div className="box-border flex w-full min-w-0 max-w-full flex-col gap-4">
              {testEnabled && scriptIndexStatus === 'processing' && (
                <ScriptIndexProgressCard
                  message={scriptIndexMessage}
                  progress={scriptIndexProgress}
                />
              )}
              {testEnabled && scriptIndexStatus === 'failed' && (
                <AlertNotice
                  message={
                    scriptIndexMessage ??
                    'Test script indexing failed. Fix the issue and re-run Detect Repository.'
                  }
                  status="icon-error-outline"
                />
              )}
              {testEnabled &&
                scriptIndexStatus === 'completed' &&
                scriptIndexMessage && (
                  <AlertNotice
                    message={scriptIndexMessage}
                    status="icon-circle-checkmark-outline"
                  />
                )}

              <div
                id="fe-card"
                ref={exclusionsRef}
                className="min-w-0 max-w-full">
                <OnboardCard secondary className="mb-0">
                  <FileExclusionsCard
                    org={selectedOrg}
                    project=""
                    repo={selectedRepo}
                    branch={selectedBranch}
                    instrumentedFilesData={analysisResult}
                    onFilesSelected={setSelectedFiles}
                    prEmails={prEmails}
                    onPrEmailsChange={emails => {
                      setPrEmails(emails);
                      if (prEmailsError) setPrEmailsError('');
                    }}
                    autoPrEmails={autoPrEmails}
                    onAutoPrEmailsChange={emails => {
                      setAutoPrEmails(emails);
                      if (autoPrEmailsError) setAutoPrEmailsError('');
                    }}
                    skipPrEmails={skipPrEmails}
                    onSkipPrEmailsChange={val => {
                      setSkipPrEmails(val);
                      if (val) setPrEmailsError('');
                    }}
                    skipAutoPrEmails={skipAutoPrEmails}
                    onSkipAutoPrEmailsChange={val => {
                      setSkipAutoPrEmails(val);
                      if (val) setAutoPrEmailsError('');
                    }}
                    prEmailsError={prEmailsError}
                    autoPrEmailsError={autoPrEmailsError}
                  />
                </OnboardCard>
              </div>

              <OnboardCard secondary className="mb-0">
                <ScanCard
                  org={selectedOrg}
                  project=""
                  repo={selectedRepo}
                  branch={selectedBranch}
                  scanBlocked={
                    testEnabled && scriptIndexStatus === 'processing'
                  }
                  scanBlockedMessage="Test scripts are still indexing. Please wait until indexing completes."
                  onStartScan={async () => {
                    const prError =
                      !skipPrEmails && prEmails.length === 0
                        ? 'At least one reviewer email is required'
                        : '';
                    const autoPrError =
                      !skipAutoPrEmails && autoPrEmails.length === 0
                        ? 'At least one automation reviewer email is required'
                        : '';
                    setPrEmailsError(prError);
                    setAutoPrEmailsError(autoPrError);
                    if (prError || autoPrError) {
                      exclusionsRef.current?.scrollIntoView({
                        behavior: 'smooth',
                        block: 'start',
                      });
                      throw new Error('Please provide reviewer emails');
                    }

                    if (testEnabled && scriptIndexStatus === 'processing') {
                      exclusionsRef.current?.scrollIntoView({
                        behavior: 'smooth',
                        block: 'start',
                      });
                      throw new Error(
                        'Test scripts are still indexing. Please wait until indexing completes.',
                      );
                    }
                    if (testEnabled && scriptIndexStatus === 'failed') {
                      exclusionsRef.current?.scrollIntoView({
                        behavior: 'smooth',
                        block: 'start',
                      });
                      throw new Error(
                        'Test script indexing failed. Re-run Detect Repository before scanning.',
                      );
                    }

                    dispatch(
                      setOnboardingEmails({
                        prEmails: skipPrEmails ? [] : prEmails,
                        autoPrEmails: skipAutoPrEmails ? [] : autoPrEmails,
                      }),
                    );
                    dispatch(
                      setOnboardingBatchInputs({
                        scmProvider: provider,
                        files: Array.from(selectedFiles),
                        patToken: pat,
                        repoUrl:
                          selectedRepoObj?.htmlUrl ||
                          `https://github.com/${selectedOrg}/${selectedRepo}`,
                        branchName: selectedBranch,
                        // Store workflow id (preferred by Actions API); name is fallback.
                        pipelineName:
                          selectedWorkflow || selectedWorkflowObj?.name || '',
                        azureProject: '',
                        repoId: dbRepoId,
                        repoName: selectedRepo,
                        branchId: dbBranchId,
                        org: selectedOrg,
                        testPatToken: testEnabled ? testPat : pat,
                        testOrg: selectedTestOrg,
                        testProject: '',
                        testPipelineName:
                          testPipelinePlatform === 'jenkins'
                            ? jenkinsJobName
                            : selectedTestWorkflow ||
                              selectedTestWorkflowObj?.name ||
                              '',
                        testRepoUrl:
                          selectedTestRepoObj?.htmlUrl ??
                          (selectedTestRepo
                            ? `https://github.com/${selectedTestOrg}/${selectedTestRepo}`
                            : ''),
                        testBranchName: selectedTestBranch,
                        testPipelinePlatform: testPipelinePlatform || '',
                        jenkinsBaseUrl,
                        jenkinsUsername,
                        jenkinsJobName,
                        isFrontend: analysisResult?.isFrontend ?? false,
                      }),
                    );

                    navigate('/sia/components/process-files');
                    return true;
                  }}
                />
              </OnboardCard>
            </div>
          )}
      </OnboardingPage>
    </OnboardBgWrapper>
  );
};

export default GitHubOnboardingView;
